import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { listPatientsApi, type PatientOut } from "@/portals/admin/api/adminPatientsApi";
import { useAdminAuthStore } from "@/stores/adminAuthStore";

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 350;

interface AdminPatientsContextValue {
  patients: PatientOut[];
  total: number;
  page: number;
  limit: number;
  search: string;
  loading: boolean;
  error: string | null;
  setPage: (page: number) => void;
  setSearch: (search: string) => void;
  refetch: () => void;
}

const AdminPatientsContext = createContext<AdminPatientsContextValue | null>(null);

export function AdminPatientsProvider({ children }: { children: ReactNode }) {
  const accessToken = useAdminAuthStore((s) => s.accessToken);
  const [patients, setPatients] = useState<PatientOut[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Debounce the search box so we don't hit the backend on every keystroke.
  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [search]);

  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    listPatientsApi({ page, limit: PAGE_SIZE, search: debouncedSearch || undefined })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (data) {
          setPatients(data.data);
          setTotal(data.total);
        } else {
          setError(error ?? "Unable to load patients.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [accessToken, page, debouncedSearch, refreshKey]);

  const refetch = useCallback(() => setRefreshKey((k) => k + 1), []);

  const value = useMemo(
    () => ({ patients, total, page, limit: PAGE_SIZE, search, loading, error, setPage, setSearch, refetch }),
    [patients, total, page, search, loading, error, refetch],
  );

  return <AdminPatientsContext.Provider value={value}>{children}</AdminPatientsContext.Provider>;
}

export function useAdminPatients() {
  const context = useContext(AdminPatientsContext);
  if (!context) {
    throw new Error("useAdminPatients must be used within AdminPatientsProvider");
  }
  return context;
}
